import * as React from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import * as moment from 'moment';
import * as R from 'ramda';

import ProfileImage from '@common/components/profile-image';
import mergeNamesText from '@common/utils/merged-names-text';
import Overview from '@common/components/overview';
import { Table } from '@common/components/table';
import Permission from '@common/components/permission';
import Dropdown from '@common/components/dropdown';
import Confirm from '@common/components/confirm-button';
import SearchBar from '@common/components/search-bar';
import { Button } from '@common/components/button';
import AdminsForm from '@modules/admin/forms/admins';
import AdminForm from '@modules/admin/forms/admin/admin';

import { EPermissions } from '@common/definitions';
import LinkWrapper from '@common/components/linkWrapper';
import * as userSelector from '../../../core/selectors/logged-user';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';

require('./styles.scss');

const OrganisationSettingsAdminsContainer = ({
  loggedUser, permissions, organisation, roles, admins, fetchRoles, fetchAdmins, fetchPermissionGroups, removeAdmin, t,
}) => {
  const [search, setSearch] = React.useState(null);
  const [editing, setEditing] = React.useState(null);

  const columns = [
    { size: 35 },
    { label: t('organisation:settings_admins_name') },
    { label: t('organisation:settings_admins_last_edited') },
    { label: t('organisation:settings_admins_roles') },
  ];

  React.useEffect(() => {
    fetchRoles();
  }, []);

  const admin = R.find(R.propEq('id', editing), admins);

  return (
    <Overview>
      {admin && (
        <AdminForm
          show={!!editing}
          admin={admin}
          roles={roles}
          onClose={() => setEditing(null)}
          fetchGroups={fetchPermissionGroups}
          removeAdmin={removeAdmin}
        />
      )}
      <Overview.Header>
        <Overview.Header.Title><Trans i18nKey="organisation:settings_admins_title" /></Overview.Header.Title>
        <div className="pull-right">
          <SearchBar defaultValue={search} onSearch={setSearch} debounce={false} filter={false} />
        </div>
        <Permission name={EPermissions.ORGANISATION_ORGANISATION_ADMINS_ADD}>
          <div className="pull-right">
            <AdminsForm
              organisation={organisation}
              admins={admins}
              roles={roles}
              fetchGroups={fetchPermissionGroups}
              onSuccess={fetchAdmins}
            >
              <Button type="primary" iconRight="add" size="large">
                <Trans i18nKey="organisation:settings_admins_add_button" />
              </Button>
            </AdminsForm>
          </div>
        </Permission>
      </Overview.Header>
      <Overview.Content>
        <Table
          columns={columns}
          items={search ? admins.filter((item) => item.full_name.toLowerCase().includes(search.toLowerCase())) : admins}
          renderRow={({ item }) => [
            <LinkWrapper url={`/admin/users/${item.id}/about`}>
              <ProfileImage enablePopover size={35} user={item} />
            </LinkWrapper>,
            <LinkWrapper url={`/admin/users/${item.id}/about`} className="tw-text-inherit hover:tw-text-inherit hover:tw-underline">
              <span className="fs-exclude">{item.full_name}</span>
            </LinkWrapper>,
            item.last_modified && (
              <>
                {item.last_modified.added_by && <div>{item.last_modified.added_by.full_name}</div>}
                <small>{moment(item.last_modified.added_at).format('D MMMM YYYY')}</small>
              </>
            ),
            mergeNamesText(item.roles, 3, 'name'),
          ]}
          placeholder={(
            <div className="Table__Placeholder">
              <Trans i18nKey="organisation:settings_admins_empty" />
            </div>
          )}
          ActionComponent={({ item }) => {
            if (
              item.id === loggedUser.id ||
              (item.roles.some((role) => role.is_global) && !permissions.includes(EPermissions.GLOBAL_ORGANISATION_ADMINS_REMOVE))
            ) return null;

            return (
              <Dropdown alignRight toggle={<Button><Trans i18nKey="organisation:settings_roles_actions" /></Button>}>
                <Permission name={EPermissions.ORGANISATION_ORGANISATION_ADMINS_ADD}>
                  <Dropdown.Item onClick={() => setEditing(item.id)}>
                    <Trans i18nKey="organisation:settings_admins_actions_edit" />
                  </Dropdown.Item>
                </Permission>
                <Permission name={EPermissions.ORGANISATION_ORGANISATION_ADMINS_REMOVE}>
                  <Confirm
                    title={t('organisation:settings_admins_actions_confirm_remove', { fullName: item.full_name })}
                    description={t('organisation:settings_admins_actions_confirm_remove_description')}
                    onConfirm={() => removeAdmin(item.id)}
                  >
                    <Dropdown.Item danger><Trans i18nKey="organisation:settings_admins_actions_remove" /></Dropdown.Item>
                  </Confirm>
                </Permission>
              </Dropdown>
            );
          }}
        />
      </Overview.Content>
    </Overview>
  );
};

const mapStateToProps = (state) => ({
  loggedUser: userSelector.selected(state),
  permissions: userSelector.permissions(state),
  organisation: organisationSelector.selected(state),
  admins: organisationSelector.admins(state),
  roles: organisationSelector.roles(state).organisation,
});

const mapDispatchToProps = {
  fetchRoles: require('../../../organisation/actions').fetchRoles,
  fetchPermissionGroups: require('../../../organisation/actions').fetchPermissionGroups,
  fetchAdmins: require('../../../organisation/actions').fetchAdmins,
  removeAdmin: require('../../actions').removeAdmin,
};

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps,
)(pageWrapper(EEventNames.VISITED_ORGANISATION_ADMINS_PAGE)(OrganisationSettingsAdminsContainer)));
